import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  CalendarDays,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  CopyPlus,
  MapPin,
  Sparkles,
  Users,
} from "lucide-react";
import { db } from "../../../firebase";
import { listStaffUsers, type StaffUser } from "../../admin/services/staffRoles";

type Template = {
  id: string;
  title: string;
  dayOfWeek: number;
  startTime: string;
  durationMinutes: number;
  timezone: string;
  coachName: string;
  coachId: string;
  capacity: number;
  location: string;
  paygPriceMinor?: number;
  paygProductName?: string;
  isActive: boolean;
};

type TemplateForm = {
  title: string;
  dayOfWeek: number;
  startTime: string;
  durationMinutes: number;
  timezone: string;
  coachId: string;
  coachName: string;
  capacity: number;
  location: string;
  paygPriceMinor: number;
  paygProductName: string;
  isActive: boolean;
};

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const quickPresets = [
  { label: "HYROX", title: "HYROX", durationMinutes: 60, capacity: 18, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { label: "Strength", title: "Strength", durationMinutes: 60, capacity: 16, paygPriceMinor: 1800, paygProductName: "STRENGTH PAYG" },
  { label: "Conditioning", title: "Conditioning", durationMinutes: 45, capacity: 20, paygPriceMinor: 1600, paygProductName: "CONDITIONING PAYG" },
  { label: "Run Club", title: "Run Club", durationMinutes: 45, capacity: 24, paygPriceMinor: 1200, paygProductName: "RUN CLUB PAYG" },
];

const starterWeekPreset = [
  { dayOfWeek: 1, title: "HYROX", startTime: "06:00", durationMinutes: 60, capacity: 18, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { dayOfWeek: 1, title: "Strength", startTime: "18:00", durationMinutes: 60, capacity: 16, paygPriceMinor: 1800, paygProductName: "STRENGTH PAYG" },
  { dayOfWeek: 2, title: "HYROX", startTime: "06:00", durationMinutes: 60, capacity: 18, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { dayOfWeek: 2, title: "Conditioning", startTime: "18:00", durationMinutes: 45, capacity: 20, paygPriceMinor: 1600, paygProductName: "CONDITIONING PAYG" },
  { dayOfWeek: 3, title: "HYROX", startTime: "06:00", durationMinutes: 60, capacity: 18, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { dayOfWeek: 3, title: "Strength", startTime: "18:00", durationMinutes: 60, capacity: 16, paygPriceMinor: 1800, paygProductName: "STRENGTH PAYG" },
  { dayOfWeek: 4, title: "HYROX", startTime: "06:00", durationMinutes: 60, capacity: 18, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { dayOfWeek: 4, title: "Conditioning", startTime: "18:00", durationMinutes: 45, capacity: 20, paygPriceMinor: 1600, paygProductName: "CONDITIONING PAYG" },
  { dayOfWeek: 5, title: "HYROX", startTime: "06:00", durationMinutes: 60, capacity: 18, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { dayOfWeek: 5, title: "Run Club", startTime: "18:00", durationMinutes: 45, capacity: 24, paygPriceMinor: 1200, paygProductName: "RUN CLUB PAYG" },
  { dayOfWeek: 6, title: "HYROX", startTime: "08:00", durationMinutes: 60, capacity: 20, paygPriceMinor: 1800, paygProductName: "HYROX PAYG" },
  { dayOfWeek: 6, title: "Strength", startTime: "09:15", durationMinutes: 60, capacity: 16, paygPriceMinor: 1800, paygProductName: "STRENGTH PAYG" },
];

const defaultForm: TemplateForm = {
  title: "HYROX",
  dayOfWeek: 2,
  startTime: "06:00",
  durationMinutes: 60,
  timezone: "Europe/London",
  coachId: "",
  coachName: "Coach",
  capacity: 18,
  location: "Main Floor",
  paygPriceMinor: 1800,
  paygProductName: "HYROX PAYG",
  isActive: true,
};

function toFormState(template?: Partial<Template>): TemplateForm {
  return {
    title: template?.title ?? defaultForm.title,
    dayOfWeek: template?.dayOfWeek ?? defaultForm.dayOfWeek,
    startTime: template?.startTime ?? defaultForm.startTime,
    durationMinutes: template?.durationMinutes ?? defaultForm.durationMinutes,
    timezone: template?.timezone ?? defaultForm.timezone,
    coachId: template?.coachId ?? defaultForm.coachId,
    coachName: template?.coachName ?? defaultForm.coachName,
    capacity: template?.capacity ?? defaultForm.capacity,
    location: template?.location ?? defaultForm.location,
    paygPriceMinor: template?.paygPriceMinor ?? defaultForm.paygPriceMinor,
    paygProductName: template?.paygProductName ?? defaultForm.paygProductName,
    isActive: template?.isActive ?? defaultForm.isActive,
  };
}

function formatMoney(minor: number) {
  return `£${(minor / 100).toFixed(2)}`;
}

function fieldClassName() {
  return "w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition placeholder:text-neutral-500 focus:border-[color:rgba(136,156,255,0.28)] focus:bg-white/[0.06]";
}

export default function AdminTemplates() {
  const auth = getAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateForm>(defaultForm);
  const [coaches, setCoaches] = useState<StaffUser[]>([]);

  const functions = useMemo(() => getFunctions(undefined, "europe-west1"), []);
  const generateClassOccurrences = useMemo(
    () => httpsCallable(functions, "generateClassOccurrences"),
    [functions]
  );

  const load = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "classTemplates"),
        orderBy("dayOfWeek"),
        orderBy("startTime")
      );
      const snap = await getDocs(q);
      const rows: Template[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      }));
      setTemplates(rows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      const staff = await listStaffUsers();
      if (!alive) return;

      const adminCoaches = staff.filter((user) => user.role === "admin");
      setCoaches(adminCoaches);

      setForm((current) => {
        const existingCoach = adminCoaches.find((coach) => coach.id === current.coachId);
        if (existingCoach) {
          return {
            ...current,
            coachName: existingCoach.name || existingCoach.email || "Coach",
          };
        }

        const fallbackCoach = adminCoaches[0];
        if (!fallbackCoach) return current;

        return {
          ...current,
          coachId: fallbackCoach.id,
          coachName: fallbackCoach.name || fallbackCoach.email || "Coach",
        };
      });
    })().catch((err) => {
      console.error("Failed to load admin coaches:", err);
    });

    return () => {
      alive = false;
    };
  }, []);

  const groupedTemplates = useMemo(() => {
    return days.map((day, index) => ({
      day,
      dayIndex: index,
      items: templates.filter((template) => template.dayOfWeek === index),
    }));
  }, [templates]);

  const activeCount = templates.filter((template) => template.isActive).length;
  const paygEnabled = form.paygPriceMinor > 0;

  function slotKey(input: { title: string; dayOfWeek: number; startTime: string }) {
    return `${input.dayOfWeek}-${input.startTime}-${input.title.trim().toLowerCase()}`;
  }

  function updateForm<K extends keyof TemplateForm>(key: K, value: TemplateForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function resetForm() {
    setEditingId(null);
    setForm(toFormState());
  }

  function applyPreset(preset: (typeof quickPresets)[number]) {
    setForm((current) => ({
      ...current,
      title: preset.title,
      durationMinutes: preset.durationMinutes,
      capacity: preset.capacity,
      paygPriceMinor: preset.paygPriceMinor,
      paygProductName: preset.paygProductName,
    }));
  }

  async function saveTemplate() {
    const user = auth.currentUser;
    if (!user) return alert("Log in first.");
    if (!form.coachId) return alert("Choose an admin coach first.");

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, "classTemplates", editingId), {
          ...form,
          updatedAt: new Date(),
        });
      } else {
        await addDoc(collection(db, "classTemplates"), {
          ...form,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }

      resetForm();
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function populateStarterWeek() {
    const user = auth.currentUser;
    if (!user) return alert("Log in first.");
    if (!form.coachId) return alert("Choose an admin coach first.");

    const confirmed = window.confirm(
      "Populate a starter week? Existing matching slots will be skipped so you don’t get duplicate timetable entries."
    );
    if (!confirmed) return;

    setPopulating(true);
    try {
      const existingKeys = new Set(templates.map((template) => slotKey(template)));
      const missingSlots = starterWeekPreset.filter((slot) => !existingKeys.has(slotKey(slot)));

      if (missingSlots.length === 0) {
        alert("Starter week already matches your current timetable.");
        return;
      }

      const batch = writeBatch(db);
      missingSlots.forEach((slot) => {
        const ref = doc(collection(db, "classTemplates"));
        batch.set(ref, {
          ...slot,
          timezone: form.timezone,
          coachId: form.coachId,
          coachName: form.coachName,
          location: form.location,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      });

      await batch.commit();
      await load();
      alert(`Added ${missingSlots.length} timetable slot${missingSlots.length === 1 ? "" : "s"}.`);
    } finally {
      setPopulating(false);
    }
  }

  function startEditing(template: Template) {
    const nextForm = toFormState(template);
    const matchingCoach = coaches.find((coach) => coach.id === nextForm.coachId);

    setEditingId(template.id);
    setForm({
      ...nextForm,
      coachName: matchingCoach?.name || matchingCoach?.email || nextForm.coachName,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function toggleActive(template: Template) {
    await updateDoc(doc(db, "classTemplates", template.id), {
      isActive: !template.isActive,
      updatedAt: new Date(),
    });
    await load();
  }

  async function remove(template: Template) {
    if (!window.confirm(`Delete "${template.title}" on ${days[template.dayOfWeek]} ${template.startTime}?`)) {
      return;
    }
    await deleteDoc(doc(db, "classTemplates", template.id));
    await load();
  }

  async function generateNow() {
    const user = auth.currentUser;
    if (!user) return alert("Log in first.");

    setGenerating(true);
    try {
      const result = await generateClassOccurrences({ daysAhead: 28 });
      console.log("Generated:", result.data);
      alert("Generated next 28 days");
    } catch (e: any) {
      console.error("Callable error:", e);
      alert(
        [
          `code: ${e?.code ?? "?"}`,
          `message: ${e?.message ?? "?"}`,
          `details: ${
            typeof e?.details === "string"
              ? e.details
              : JSON.stringify(e?.details ?? null, null, 2)
          }`,
        ].join("\n\n")
      );
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[1.9rem] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.18),transparent_30%),linear-gradient(180deg,rgba(12,14,22,0.98),rgba(8,9,14,0.98))] p-5 sm:p-8">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(142,163,255,0.45)] to-transparent" />

          <div className="relative z-10 grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_360px]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(136,156,255,0.18)] bg-[color:rgba(59,84,170,0.18)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:#dbe6ff]">
                <Sparkles className="h-3.5 w-3.5" />
                Timetable Creator
              </div>

              <h1 className="mt-4 text-3xl font-heading tracking-tight sm:text-5xl">
                Build the weekly timetable without wrestling the UI
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-neutral-400 sm:text-base">
                Create recurring weekly slots, assign the right coach, and control PAYG pricing
                before generating the next 28 days of classes.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {quickPresets.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/72 transition hover:border-[color:rgba(136,156,255,0.24)] hover:bg-[color:rgba(59,84,170,0.16)] hover:text-[color:#dbe6ff]"
                  >
                    {preset.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={populateStarterWeek}
                  disabled={populating}
                  className="rounded-full border border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.16)] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color:#dbe6ff] transition hover:bg-[color:rgba(59,84,170,0.24)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {populating ? "Populating..." : "Populate starter week"}
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Templates</div>
                <div className="mt-2 text-3xl font-semibold text-white">{templates.length}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Active slots</div>
                <div className="mt-2 text-3xl font-semibold text-white">{activeCount}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">PAYG status</div>
                <div className="mt-2 text-3xl font-semibold text-white">
                  {paygEnabled ? formatMoney(form.paygPriceMinor) : "Off"}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/35 p-4 backdrop-blur">
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Generate window</div>
                <div className="mt-2 text-3xl font-semibold text-white">28 days</div>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_360px]">
          <section className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-heading tracking-tight">
                  {editingId ? "Edit weekly slot" : "Create weekly slot"}
                </h2>
                <p className="mt-2 text-sm leading-6 text-neutral-400">
                  Set the core details once and the generator will turn them into scheduled classes.
                </p>
              </div>

              {editingId ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/72 transition hover:bg-white/[0.06]"
                >
                  Cancel edit
                </button>
              ) : null}
            </div>

            <div className="mt-6 space-y-6">
              <div>
                <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff]">
                  Day of week
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
                  {days.map((day, index) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => updateForm("dayOfWeek", index)}
                      className={[
                        "rounded-2xl border px-4 py-3 text-sm font-semibold transition",
                        form.dayOfWeek === index
                          ? "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]"
                          : "border-white/10 bg-white/[0.03] text-white/72 hover:bg-white/[0.05]",
                      ].join(" ")}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-300">Class title</label>
                  <input
                    className={fieldClassName()}
                    value={form.title}
                    onChange={(e) => updateForm("title", e.target.value)}
                    placeholder="HYROX"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-300">Coach</label>
                  <select
                    className={fieldClassName()}
                    value={form.coachId}
                    onChange={(e) => {
                      const selectedCoach = coaches.find((coach) => coach.id === e.target.value);
                      setForm((current) => ({
                        ...current,
                        coachId: e.target.value,
                        coachName: selectedCoach?.name || selectedCoach?.email || "Coach",
                      }));
                    }}
                  >
                    {coaches.length === 0 ? (
                      <option value="">No admin coaches available</option>
                    ) : (
                      coaches.map((coach) => (
                        <option key={coach.id} value={coach.id}>
                          {coach.name || coach.email || coach.id}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-300">Start time</label>
                  <input
                    type="time"
                    className={fieldClassName()}
                    value={form.startTime}
                    onChange={(e) => updateForm("startTime", e.target.value)}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-300">Duration</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[45, 60, 75].map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() => updateForm("durationMinutes", minutes)}
                        className={[
                          "rounded-2xl border px-3 py-3 text-sm font-semibold transition",
                          form.durationMinutes === minutes
                            ? "border-[color:rgba(136,156,255,0.24)] bg-[color:rgba(59,84,170,0.18)] text-[color:#dbe6ff]"
                            : "border-white/10 bg-white/[0.03] text-white/72 hover:bg-white/[0.05]",
                        ].join(" ")}
                      >
                        {minutes}m
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-300">Capacity</label>
                  <input
                    type="number"
                    className={fieldClassName()}
                    value={form.capacity}
                    onChange={(e) => updateForm("capacity", Number(e.target.value))}
                    min={1}
                    max={60}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-300">Location</label>
                  <input
                    className={fieldClassName()}
                    value={form.location}
                    onChange={(e) => updateForm("location", e.target.value)}
                    placeholder="Main Floor"
                  />
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-4 sm:p-5">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff]">
                      PAYG and publishing
                    </div>
                    <p className="mt-2 text-sm text-neutral-400">
                      Only turn PAYG on when this slot should be sold outside memberships.
                    </p>
                  </div>

                  <label className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm font-medium text-white/80">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => updateForm("isActive", e.target.checked)}
                    />
                    Active slot
                  </label>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-neutral-300">PAYG price (pence)</label>
                    <input
                      type="number"
                      className={fieldClassName()}
                      value={form.paygPriceMinor}
                      onChange={(e) => updateForm("paygPriceMinor", Number(e.target.value) || 0)}
                      min={0}
                      step={50}
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-neutral-300">PAYG label</label>
                    <input
                      className={fieldClassName()}
                      value={form.paygProductName}
                      onChange={(e) => updateForm("paygProductName", e.target.value)}
                      placeholder="HYROX PAYG"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-neutral-300">Timezone</label>
                    <input
                      className={fieldClassName()}
                      value={form.timezone}
                      onChange={(e) => updateForm("timezone", e.target.value)}
                      placeholder="Europe/London"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={saveTemplate}
                  disabled={saving}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#89a1ff,#4b63c9)] px-5 py-3 font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <Check className="h-4 w-4" />
                  {saving ? "Saving..." : editingId ? "Save weekly slot" : "Create weekly slot"}
                </button>

                <button
                  type="button"
                  onClick={generateNow}
                  disabled={generating}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[color:rgba(136,156,255,0.16)] bg-[color:rgba(59,84,170,0.14)] px-5 py-3 font-semibold text-[color:#dbe6ff] transition hover:bg-[color:rgba(59,84,170,0.22)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <CopyPlus className="h-4 w-4" />
                  {generating ? "Generating..." : "Generate next 28 days"}
                </button>
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <section className="rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-5">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[color:#aabaff]">
                Live summary
              </div>

              <div className="mt-4 space-y-4">
                <div className="rounded-2xl border border-[color:rgba(136,156,255,0.12)] bg-[color:rgba(59,84,170,0.14)] p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/42">Slot preview</div>
                  <div className="mt-2 text-2xl font-heading text-white">
                    {form.title}
                  </div>
                  <div className="mt-3 text-sm leading-6 text-white/66">
                    {days[form.dayOfWeek]} at {form.startTime} for {form.durationMinutes} minutes
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                    <div className="flex items-center gap-2 text-[color:#aabaff]">
                      <Users className="h-4 w-4" />
                      <span className="text-[10px] uppercase tracking-[0.2em]">Capacity</span>
                    </div>
                    <div className="mt-3 text-2xl font-semibold text-white">{form.capacity}</div>
                  </div>

                  <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                    <div className="flex items-center gap-2 text-[color:#aabaff]">
                      <CircleDollarSign className="h-4 w-4" />
                      <span className="text-[10px] uppercase tracking-[0.2em]">PAYG</span>
                    </div>
                    <div className="mt-3 text-2xl font-semibold text-white">
                      {paygEnabled ? formatMoney(form.paygPriceMinor) : "Off"}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 text-[color:#aabaff]">
                    <MapPin className="h-4 w-4" />
                    <span className="text-[10px] uppercase tracking-[0.2em]">Location & coach</span>
                  </div>
                  <div className="mt-3 text-sm leading-6 text-white/70">
                    {form.location}
                    <br />
                    {form.coachName || "Coach not set"}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 text-[color:#aabaff]">
                    <Clock3 className="h-4 w-4" />
                    <span className="text-[10px] uppercase tracking-[0.2em]">Publishing state</span>
                  </div>
                  <div className="mt-3 text-sm leading-6 text-white/70">
                    {form.isActive ? "This slot will be included in future generation runs." : "Paused slots stay in the timetable but won’t generate new classes."}
                  </div>
                </div>
              </div>
            </section>
          </aside>
        </div>

        <section className="mt-6 rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,13,18,0.98),rgba(8,9,12,0.98))] p-5 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-heading tracking-tight">Weekly timetable</h2>
              <p className="mt-2 text-sm leading-6 text-neutral-400">
                Review and adjust recurring slots day by day before generating classes.
              </p>
            </div>

            <button
              onClick={load}
              className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/72 transition hover:bg-white/[0.06]"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div className="mt-6 grid gap-4 xl:grid-cols-2">
            {groupedTemplates.map(({ day, items }) => (
              <div
                key={day}
                className="rounded-[1.5rem] border border-white/8 bg-white/[0.02] p-4"
              >
                <div className="flex items-center justify-between gap-3 border-b border-white/8 pb-3">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-[color:#aabaff]" />
                    <h3 className="text-lg font-semibold text-white">{day}</h3>
                  </div>
                  <div className="text-xs uppercase tracking-[0.18em] text-white/36">
                    {items.length} slot{items.length === 1 ? "" : "s"}
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-white/10 px-4 py-5 text-sm text-white/44">
                      No classes scheduled for {day} yet.
                    </div>
                  ) : (
                    items.map((template) => (
                      <div
                        key={template.id}
                        className="rounded-2xl border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))] p-4"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-base font-semibold text-white">{template.title}</div>
                              <span
                                className={[
                                  "inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]",
                                  template.isActive
                                    ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                                    : "border-white/10 bg-white/[0.04] text-white/60",
                                ].join(" ")}
                              >
                                {template.isActive ? "Active" : "Paused"}
                              </span>
                            </div>

                            <div className="mt-2 text-sm leading-6 text-white/64">
                              {template.startTime} • {template.durationMinutes} min • {template.coachName}
                              <br />
                              {template.location} • Cap {template.capacity} •{" "}
                              {Number(template.paygPriceMinor ?? 0) > 0
                                ? `PAYG ${formatMoney(template.paygPriceMinor ?? 0)}`
                                : "PAYG off"}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => startEditing(template)}
                              className="rounded-full border border-[color:rgba(136,156,255,0.20)] bg-[color:rgba(59,84,170,0.16)] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color:#dbe6ff]"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => toggleActive(template)}
                              className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/72"
                            >
                              {template.isActive ? "Pause" : "Activate"}
                            </button>
                            <button
                              onClick={() => remove(template)}
                              className="rounded-full border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-red-200"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>

          {templates.length > 0 ? (
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/44">
              <ChevronRight className="h-4 w-4" />
              Existing generated classes keep their current snapshot until edited separately.
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
